/**
 * Menu Item Component
 *
 * Renders a single menu item with optional children (submenu).
 * Supports internal React routes and external links.
 * 100% React/Python - sem redirecionamento para Yii.
 *
 * @since 2025-12-16
 * @epic INIT-0045, INIT-0046
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { NavMenuItem } from '@/types/menu';
import { MenuIconSvg } from './MenuIcon';
import { useUIStore } from '@/store';
import { useTheme } from '@/theme/ThemeContext';
import { useActiveMenu } from './activeMenu';

interface MenuItemProps {
  item: NavMenuItem;
  collapsed?: boolean;
  level?: number;
  onItemClick?: (item: NavMenuItem) => void;
}

/**
 * Menu Item component.
 *
 * Renders a navigation item with support for:
 * - Internal React routes
 * - External links (opens in new tab)
 * - Nested submenus with expand/collapse
 * - Active state highlighting
 * - Collapsed state (icon only)
 */
export function MenuItem({
  item,
  collapsed = false,
  level = 0,
  onItemClick,
}: MenuItemProps) {
  const menuRef = useRef<HTMLLIElement>(null);
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { theme } = useTheme();
  const { activeId, ancestorIds } = useActiveMenu();
  // Menu sempre inicia fechado - usuario controla abertura manualmente
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Cores do menu vindas das CSS variables (respeitam dark mode automaticamente)
  const root = typeof document !== 'undefined' ? document.documentElement : null;
  const getCssVar = (name: string, fallback: string) =>
    root?.style.getPropertyValue(name)?.trim() || fallback;

  const menuColors = theme?.menus?.main;
  // Defaults otimizados para sidebar escura (--brand-primary #18344f).
  // CSS vars em :root garantem que funcione mesmo sem tema do cliente injetado.
  const menuText = getCssVar('--sidebar-menu-foreground', menuColors?.text || 'rgba(255, 255, 255, 0.82)');
  const menuHoverBg = getCssVar('--sidebar-menu-hover', menuColors?.hoverBackground || 'rgba(255, 255, 255, 0.12)');
  const menuHoverText = getCssVar('--sidebar-menu-hover-foreground', menuColors?.hoverText || 'var(--brand-primary-foreground, #ffffff)');
  const menuActiveBg = getCssVar('--sidebar-menu-active', menuColors?.activeBackground || theme?.blocks?.block2?.background || '#e35b00');
  const menuActiveText = getCssVar('--sidebar-menu-active-foreground', menuColors?.activeText || 'var(--brand-accent-foreground, #ffffff)');
  const menuBorder = menuColors?.border || 'rgba(255, 255, 255, 0.15)';

  // Cor secundária do tema para estado ativo (fallback)
  const secondaryColor = menuActiveBg;

  const hasChildren = item.children && item.children.length > 0;

  // Fecha submenu ao clicar fora (quando colapsado)
  useEffect(() => {
    if (!collapsed || !isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [collapsed, isExpanded]);

  // Active state resolved centrally in DynamicSidebar via ActiveMenuContext.
  // Garante que apenas UM item da árvore inteira é marcado como ativo, mesmo
  // quando há paths duplicados no menu vindo do backend (TMF9-53667).
  const isActive = activeId != null && item.id === activeId;
  const hasActiveChild = ancestorIds.has(item.id);

  // Menu so abre quando usuario clica - nao abre automaticamente
  const isOpen = isExpanded;

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      // Se sidebar está colapsada, expandir a sidebar ao clicar em qualquer item
      if (sidebarCollapsed) {
        e.preventDefault();
        toggleSidebarCollapsed();
        return;
      }

      if (hasChildren) {
        e.preventDefault();
        setIsExpanded(!isExpanded);
      }
      onItemClick?.(item);
    },
    [hasChildren, isExpanded, item, onItemClick, sidebarCollapsed, toggleSidebarCollapsed]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (hasChildren) {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }
      }
      if (e.key === 'ArrowRight' && hasChildren && !isOpen) {
        e.preventDefault();
        setIsExpanded(true);
      }
      if (e.key === 'ArrowLeft' && hasChildren && isOpen) {
        e.preventDefault();
        setIsExpanded(false);
      }
    },
    [hasChildren, isExpanded, isOpen]
  );

  // Base classes for menu item - layout only, colors via inline styles
  const itemClasses = clsx(
    'flex items-center transition-all duration-200 cursor-pointer border-0',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    collapsed
      ? 'justify-center w-10 h-10 mx-auto rounded-lg'
      : 'gap-3 px-3 py-2.5 rounded-xl',
    {
      'shadow-md': isActive,
      // Level indentation (for submenus when not collapsed)
      'pl-6': level === 1 && !collapsed,
      'pl-9': level === 2 && !collapsed,
      'pl-12': level >= 3 && !collapsed,
    }
  );

  // Inline styles based on theme menu colors (including hover)
  // Pai com filho ativo (hasActiveChild) usa destaque sutil (bold + cor de hover)
  // — sem bg fill — para não competir visualmente com o item realmente ativo
  // e garantir contraste WCAG AA em dark mode [TMF9-53667]
  const itemStyle: React.CSSProperties = isActive
    ? {
        backgroundColor: menuActiveBg,
        color: menuActiveText,
        boxShadow: `0 4px 6px -1px ${menuActiveBg}40`,
      }
    : hasActiveChild
      ? {
          color: menuHoverText,
          fontWeight: 600,
        }
      : isHovered
        ? {
            backgroundColor: menuHoverBg,
            color: menuHoverText,
          }
        : {
            color: menuText,
          };

  // Icon color based on state
  const iconStyle: React.CSSProperties = isActive
    ? { color: menuActiveText }
    : hasActiveChild
      ? { color: menuHoverText }
      : { color: menuText };

  // Render the menu content
  // Ícones só aparecem no primeiro nível (level === 0)
  // Submenus (level > 0) mostram apenas texto para UI mais limpa
  const showIcon = level === 0;

  const menuContent = (
    <>
      {showIcon && (
        <span className="transition-colors" style={iconStyle}>
          <MenuIconSvg icon={item.icon} size="md" />
        </span>
      )}
      {!collapsed && (
        <>
          <span className="font-medium flex-1 truncate">
            {item.label}
          </span>
          {hasChildren && (
            <svg
              className={clsx(
                'w-4 h-4 transition-transform duration-200',
                isOpen && 'rotate-90',
              )}
              style={{ opacity: isActive ? 0.8 : 0.5 }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          )}
        </>
      )}
    </>
  );

  // Hover handlers for theme-based hover colors
  const hoverHandlers = {
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  // Render external link
  if (item.isExternal) {
    return (
      <li className="group">
        <a
          href={item.path}
          target={item.target || '_blank'}
          rel="noopener noreferrer"
          className={itemClasses}
          style={itemStyle}
          title={collapsed ? item.label : undefined}
          onClick={() => onItemClick?.(item)}
          {...hoverHandlers}
        >
          {menuContent}
          {!collapsed && (
            <svg
              className="w-3 h-3"
              style={{ opacity: 0.5 }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          )}
        </a>
      </li>
    );
  }

  // Render with children (expandable)
  if (hasChildren) {
    return (
      <li ref={menuRef} className="relative group">
        <div
          role="button"
          tabIndex={0}
          className={itemClasses}
          style={itemStyle}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          title={collapsed ? item.label : undefined}
          aria-expanded={isOpen}
          aria-haspopup="true"
          {...hoverHandlers}
        >
          {menuContent}
        </div>

        {/* Submenu */}
        {!collapsed && isOpen && (
          <ul
            className="mt-1 ml-2 space-y-0.5 border-l-2 pl-2"
            style={{ borderColor: menuBorder }}
            role="menu"
          >
            {item.children.map((child) => (
              <MenuItem
                key={child.id}
                item={child}
                collapsed={collapsed}
                level={level + 1}
                onItemClick={onItemClick}
              />
            ))}
          </ul>
        )}

      </li>
    );
  }

  // Render simple internal link
  return (
    <li className="group">
      <Link
        to={item.path}
        className={itemClasses}
        style={itemStyle}
        title={collapsed ? item.label : undefined}
        onClick={() => onItemClick?.(item)}
        {...hoverHandlers}
      >
        {menuContent}
      </Link>
    </li>
  );
}

export default MenuItem;
